import { useTranslations } from '@vocab/react';
import { Box, Pagination, Stack, Tiles } from 'braid-design-system';
import { useEffect, useRef, useState } from 'react';

import { Section, type Jobs } from '../../../';
import type { PaginationType } from '../../../analytics/types';

import translations from './.vocab';
import { JobCard } from './JobCard';

interface Props {
  getPaginatedJobs: (organisationId: string, page?: number) => Promise<Jobs>;
  mode: 'view' | 'edit';
  model: Jobs;
  trackJobsPaginationClicked: ({
    searchTotalCount,
    pageNumber,
  }: {
    pageNumber: number;
    searchTotalCount?: number;
  }) => void;
}

export function JobsSection({
  mode,
  model,
  getPaginatedJobs,
  trackJobsPaginationClicked,
}: Props) {
  const [currentPage, setCurrentPage] = useState(1);
  const scrollRef = useRef<HTMLInputElement>(null);
  const [jobs, setJobs] = useState(model?.jobs || []);
  const [totalJobs, setTotalJobs] = useState(0);
  const { t } = useTranslations(translations);

  const scrollToRef = () =>
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    async function loadJobs() {
      try {
        if (model?.organisationId) {
          const { jobs: result, totalJobs: totalJobCount } =
            await getPaginatedJobs(model.organisationId, currentPage);
          setJobs(result);
          setTotalJobs(totalJobCount);
        } else {
          setJobs(model?.jobs || []);
          setTotalJobs(model?.totalJobs || 0);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading jobs', {
          organisationId: model?.organisationId,
          error,
        });
        setJobs(model?.jobs || []);
        setTotalJobs(model?.totalJobs || 0);
      }
    }
    void loadJobs();
  }, [currentPage, getPaginatedJobs, model]);

  const totalPageCount = Math.ceil(totalJobs / model?.jobs.length || 1);

  const onPaginationLinkClick =
    (_: PaginationType, pageNumber: number) =>
    (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      e.preventDefault();
      trackJobsPaginationClicked({
        pageNumber,
        searchTotalCount: totalJobs,
      });
      setCurrentPage(pageNumber);
      setTimeout(scrollToRef, 300);
    };

  return (
    <Section mode={mode} padding="none" paddingBottom="none">
      <Box paddingX={['none', 'none', 'large']} ref={scrollRef}>
        <Stack space="xlarge">
          <Tiles columns={[1, 2]} space="small">
            {jobs.map((job) => (
              <JobCard {...job} key={job.id} />
            ))}
          </Tiles>
          {totalPageCount > 1 && (
            <Pagination
              page={currentPage}
              total={totalPageCount}
              linkProps={({ page, type }) => ({
                href: `#`,
                onClick: onPaginationLinkClick(type, page),
              })}
              label={t('Jobs pagination')}
              nextLabel={t('Next')}
              previousLabel={t('Previous')}
            />
          )}
        </Stack>
      </Box>
    </Section>
  );
}
